import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "../styles/contents.module.css";
import cn from "classnames";
import {
  HeadingOneBlock,
  HeadingThreeBlock,
  HeadingTwoBlock,
  SlateBlock,
  generateBlockId,
} from "@atlas/renderer";
import ContentsIcon from "@atlas/uicorn/lib/Icon/ContentsIcon";
import window from "@atlas/tool/box/browser/window";
import contentStyles from "../styles/content.module.css";
import { debounce } from "lodash-es";
import useRefCallback from "use-ref-callback";

export default function Contents(props: TContentsProps) {
  const { nodes, onChangeActive } = props;

  const idsMap: string[] = [];

  const headers = nodes
    .filter(
      (node): node is HeadingOneBlock | HeadingTwoBlock | HeadingThreeBlock =>
        headingTypes.includes(node.type)
    )
    .map((node) => {
      const text = node.children.map((leaf) => leaf.text).join("");
      const id = generateBlockId(node, idsMap);
      const nested = node.type === "heading-three";
      return { id, text, nested };
    })
    .filter((headerObject) => headerObject.text); // avoid line breaks stored as H*

  const [active, setActive] = useState<string | null>(() => {
    if (!headers.length) return null;

    const browserSelected = window?.location.hash.slice(1);
    if (browserSelected) {
      if (headers.some((header) => header.id === browserSelected)) {
        return browserSelected;
      }
    }

    return headers[0].id || null;
  });

  const ignoreViewportChangeRef = useRef(false);
  const setNextActive = useCallback((id: string) => {
    ignoreViewportChangeRef.current = true;
    setActive(id);
    setTimeout(() => {
      ignoreViewportChangeRef.current = false;
    }, 500); // Wait for browser scroll to finish before triggering next handler
  }, []);

  useLayoutEffect(() => {
    const scrollHandler = debounce(() => {
      const headerElements = Array.from(
        document.querySelectorAll<HTMLElement>(
          `.${contentStyles.container} h2[id], .${contentStyles.container} h3[id]`
        )
      );

      if (ignoreViewportChangeRef.current) return;

      for (let i = 0; i < headerElements.length; i++) {
        const el = headerElements[i];
        const top = el.getBoundingClientRect().top;

        if (top >= 0 && top < 1) return setActive(el.id);

        if (top > 0) {
          if (i === 0) return setActive(el.id);
          return setActive(headerElements[i - 1].id);
        }

        if (i === headerElements.length - 1) {
          return setActive(el.id);
        }
      }

      setActive(headerElements[0]?.id || null);
    }, 20);

    scrollHandler();

    window?.addEventListener("scroll", scrollHandler);
    window?.addEventListener("resize", scrollHandler);

    return () => {
      window?.removeEventListener("scroll", scrollHandler);
      window?.removeEventListener("resize", scrollHandler);
    };
  }, [headers]);

  const markerStyle = useMemo<React.CSSProperties>(() => {
    const index = active
      ? headers.findIndex((header) => header.id === active)
      : -1;

    if (index < 0) return { top: -1e5 };

    return { top: (20 + 12) * index + (22 + 16) + 1 }; // Links offset + Header offset
  }, [active, headers]);

  const onChangeActiveCallback = useRefCallback(onChangeActive);

  useEffect(() => {
    onChangeActiveCallback?.(active);
  }, [active, onChangeActiveCallback]);

  if (!headers.length) return null;

  return (
    <menu className={styles.contentsContainer}>
      <h5 className={styles.contentsHeader}>
        <ContentsIcon />
        Table of contents
      </h5>
      {headers.map((header) => {
        return (
          <li key={header.id}>
            <a
              className={cn(styles.contentsLink, {
                [styles.nestedLink]: header.nested,
                [styles.activeLink]: header.id === active,
              })}
              href={`#${header.id}`}
              onClick={() => setNextActive(header.id)}
            >
              {header.text}
            </a>
          </li>
        );
      })}
      <span className={styles.contentsMarker} style={markerStyle} />
    </menu>
  );
}

const headingTypes: SlateBlock["type"][] = [
  "heading-one",
  "heading-two",
  "heading-three",
];

type TContentsProps = {
  nodes: SlateBlock[];
  onChangeActive?: (activeId: string | null) => void;
};
